'use client'

import { useEffect, useRef, useState } from "react"
import { AddAddress } from "@/components/common/AddAddress" // Adjust the import path as needed
import useCommonStore from "@/store/commonStore"
import { toast } from "@/hooks/use-toast"
import { removeAddress } from "@/lib/cart"
import LocationMap from "../common/LocationMap"
import Loader from "../common/loader"
import { set } from "zod"


interface IAddress {
  address_id: string
  full_nm: string
  phn_num: string
  phn_cd: string
  email: string
  locality: string
  city: string
  state: string
  country: string
  zip_code: string
  is_default: boolean
  address_name: string
  address_line1: string
  address_line2: string
  lat: number
  lng: number
  loc_str: string
}

export default function AddressList() {
  const [modalOpen, setModalOpen] = useState(false)
  const [addressId, setAddressId] = useState<string>("")
  const [loading, setLoading] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState<Record<string, any>>({})
  const modalRef = useRef<HTMLDivElement>(null)
  const [locationMapOpen, setLocationMapOpen] = useState(false)
  const { setAddress, fetchAddressList, addressList, user,translations } = useCommonStore()

  useEffect(() => {
    fetchAddresses()
  }, [])

  const fetchAddresses = async () => {
    setLoading(true)
    const response = await fetchAddressList()
    setLoading(false)
    if (!response.status) {
      toast({
        title: "Error",
        description: response?.error || response.message,
        variant: "destructive"
      })
    }
  }

  const handleEdit = (add_id: string) => {
    setAddressId(add_id)
    setLocationMapOpen(true);
  }
  const handleDelete = async(address_id: string) => {
    setLoading(true)
    const result = await removeAddress({ address_id })
    setLoading(false)
    fetchAddresses()
    if (result?.status) {
      toast({
        title: "Success",
        description: result?.message || result?.message,
        variant: "default"
      })

    } else {
      toast({
        title: "Error",
        description: result?.error || result?.message,
        variant: "destructive"
      })
    }
  }

  const handleAdd = () => {
    setAddressId("")
    setLocationMapOpen(true);
  }

  const handleLocationConfirm = (doc: Record<string, any>,center: { lat: number, lng: number }) => {    
    let obj={
      address_line2:"",
      locality:"",
      city:"",
      state:"",
      country:"",
      zip_code:"",
      lat:center.lat,
      lng:center.lng,
      loc_str:""
    }
    if (doc?.features?.length) {
      doc.features?.forEach((a: Record<string, any>) => {
        if(a.place_type.includes("address")){
          obj.address_line2=a.text;
          obj.loc_str=a.place_name;
        }
        if(a.place_type.includes("neighborhood")){
          obj.locality=a.text;
        }
        if (a.place_type.includes("locality") && !obj.locality) {
          obj.locality = a.text;
        }
        if(a.place_type.includes("place")){
          obj.city=a.text;
        }
        if(a.place_type.includes("region")){
          obj.state=a.text;
        }
        if(a.place_type.includes("country")){
          let code = a?.properties?.short_code.toUpperCase();
          obj.country=code?code:a.text;
        }
        if(a.place_type.includes("postcode")){
          obj.zip_code=a.text;
        }
      })
      if(!obj.locality){
        obj.locality=obj.city||""
      }
      if(!obj.loc_str){
        obj.loc_str=obj?.address_line2||""
      }
    }
    setSelectedAddress(obj);
    setLocationMapOpen(false);
    setModalOpen(true);
  }



  return (
    <div>
{
  loading?(
    <Loader/>
  ):(
          <>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl md:text-6xl font-bold text-chart-3">Address list</h1>
        <button
          className="bg-primary text-white px-6 py-2 rounded font-semibold text-lg hover:bg-[#4d2d0e] transition"
          onClick={handleAdd}
        >
          {translations?.["add_address"]||"Add Address"}
        </button>
      </div>
      {addressList?.length > 0 ? (
        <>
          {addressList?.map((addr: IAddress) => (
            <div
              key={addr.address_id}
              className="bg-white rounded-xl shadow p-6 mb-8 border flex flex-col gap-2 relative"
              style={{ maxWidth: 900 }}
            >
              <span className="absolute right-6 top-6 text-[#7AD1C2] font-semibold rtl:left-6 rtl:right-auto">{addr.address_name}</span>
              <div className="font-bold text-lg mb-1">{addr.full_nm}</div>
              <div className="mb-1">{addr.address_line1}, {addr.city}, {addr.locality}, {addr.zip_code}</div>
              <div className="text-gray-400 mb-2">
                {translations?.["mbl_nm"]||"Mobile"}: <span className="text-black">{addr.phn_cd}{addr.phn_num}</span>
              </div>
              <div className="flex gap-4 mt-2">
                <button className="border border-primary text-primary px-8 py-2 rounded font-semibold hover:bg-[#f7ece6] transition"
                    onClick={(e) => {
                    e.stopPropagation()
                    handleDelete(addr.address_id)
                  }}>
                  {translations["remove"]||"Remove"}
                </button>
                <button
                  className="bg-primary text-white px-8 py-2 rounded font-semibold hover:bg-[#4d2d0e] transition"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedAddress({lat:addr.lat,lng:addr.lng})
                    handleEdit(addr.address_id)
                  }
                  }
                >
                  {translations?.["edt"]||"Edit"}
                </button>
              </div>
            </div>
          ))}
        </>
      ) : (
        <div className="text-sm text-gray-500 min-h-[100px] flex justify-center items-center">
          {translations?.["empty_address"]||"No addresses found"}
        </div>
      )
      }
      </>
  )
}
      {modalOpen && (
        <AddAddress
        translations={translations}
          open={modalOpen}
          onClose={() => {
            setModalOpen(false)
            setAddressId("")
             setSelectedAddress({})

          }}
          onBack={()=>{
            setModalOpen(false)
            setLocationMapOpen(true)
          }}
          addressId={addressId} // Pass null or a specific address ID if editing
            selectedAddress={selectedAddress??{}}
        />
      )}
            <LocationMap
              open={locationMapOpen}
              onClose={() => {
                setAddressId("")
                setSelectedAddress({})
                setLocationMapOpen(false)
              }}
              onConfirm={handleLocationConfirm}
              initialLat={selectedAddress?.lat||parseFloat(process.env.NEXT_PUBLIC_DEAFULT_LATITUDE || "28.6139")}
              initialLng={selectedAddress?.lng||parseFloat(process.env.NEXT_PUBLIC_DEAFULT_LONGITUDE || "77.209")}
            />
    </div>
  )
}